import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Button,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./UI";
import { AlertTriangle, Trash2, UserMinus, AlertCircle, Loader2, Check, Key } from "lucide-react";
import { SettingsAPI } from "api/settings/service";
import { useAlert } from "components/UI_components/Alert";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "utils/constant";

export function DangerZoneSection() {
  const navigate = useNavigate();
  const { addAlert } = useAlert();
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [transferEmail, setTransferEmail] = useState("");

  const [user, setUser] = useState(JSON.parse(localStorage.getItem("user")) || {});
  const [password, setPassword] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleVerifyPassword = async () => {
    if (!password) {
      addAlert("Please enter your password", "destructive");
      return;
    }

    setIsLoading(true);
    try {
      const res = await SettingsAPI.login({
        email: user.user_email,
        password: password,
      });

      if (res.status) {
        setIsVerified(true);
        addAlert("Password verified successfully", "success");
      } else {
        addAlert(res.message || "Invalid password", "destructive");
      }
    } catch (error) {
      console.error("Error verifying password:", error);
      addAlert("Failed to verify password. Please try again.", "destructive");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      const refresh = localStorage.getItem("refresh");
      await fetch(`${BASE_URL}/token/blacklist/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh }),
      });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.clear();
      navigate("/login");
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== "DELETE" || !isVerified) return;

    setIsDeleting(true);
    try {
      const res = await SettingsAPI.deleteUser(user.user);

      // The API might return status or just succeed
      addAlert("Your account has been permanently deleted.", "success");

      // Delay slightly for alert to be seen
      setTimeout(() => {
        handleLogout();
      }, 2000);
    } catch (error) {
      console.error("Error deleting account:", error);
      addAlert("Failed to delete account. Please contact support.", "destructive");
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-destructive flex items-center gap-2">
          <AlertTriangle className="h-6 w-6" />
          Danger Zone
        </h2>
        <p className="text-text-gray mt-1">These actions are irreversible. Please proceed with caution.</p>
      </div>

      {/* <Card className="border-destructive/30 shadow-sm bg-destructive/5">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2 text-destructive">
            <UserMinus className="h-5 w-5" />
            Transfer Ownership
          </CardTitle>
          <CardDescription>Transfer your organization to another team member.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 rounded-lg bg-sm-accent/10 border border-sm-accent/20">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-sm-accent flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-primary-text">Important</p>
                <p className="text-sm text-text-gray mt-1">
                  The new owner must already be a team member with admin access. You'll become a regular admin after transfer.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="transferEmail">New Owner's Email</Label>
            <Input
              id="transferEmail"
              type="email"
              placeholder="Enter email of the new owner"
              value={transferEmail}
              onChange={(e) => setTransferEmail(e.target.value)}
            />
          </div>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="border-destructive text-destructive hover:bg-destructive hover:text-white">
                Transfer Ownership
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Transfer Organization Ownership</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to transfer ownership to <strong>{transferEmail || "this user"}</strong>?
                  You will lose owner privileges and become a regular admin.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction className="bg-destructive text-white hover:bg-destructive/90">
                  Yes, Transfer Ownership
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card> */}

      <Card className="border-destructive/50 shadow-sm bg-destructive/5">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2 text-destructive">
            <Trash2 className="h-5 w-5" />
            Delete Account
          </CardTitle>
          <CardDescription>Permanently delete your account and all associated data.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-destructive">This action cannot be undone</p>
                <ul className="text-sm text-text-gray mt-2 space-y-1 list-disc list-inside">
                  <li>All your projects will be permanently deleted</li>
                  <li>All team members will lose access</li>
                  <li>Your subscription will be cancelled immediately</li>
                  <li>Historical data cannot be recovered</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="password">Confirm your password</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-gray" />
                  <input
                    id="password"
                    type="password"
                    placeholder="Enter your current password"
                    className="pl-10 flex h-10 w-full rounded-lg border border-sm-border bg-input-bg pr-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-text-gray focus:outline-none focus:ring-2 focus:ring-sm-primary/20 focus:border-sm-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isVerified || isLoading}
                  />
                </div>
                <Button
                  variant={isVerified ? "outline" : "primary"}
                  onClick={handleVerifyPassword}
                  disabled={isVerified || isLoading || !password}
                  className="w-32"
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : isVerified ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    "Verify"
                  )}
                </Button>
              </div>
              {isVerified && (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <Check className="h-3 w-3" /> Password verified
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="deleteConfirm">Type "DELETE" to confirm</Label>
              <Input
                id="deleteConfirm"
                placeholder="DELETE"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                disabled={!isVerified}
                className={!isVerified ? "bg-gray-bg/50 opacity-70" : ""}
              />
            </div>
          </div>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="destructive"
                disabled={deleteConfirmation !== "DELETE" || !isVerified || isDeleting}
                className="gap-2"
              >
                {isDeleting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                {isDeleting ? "Deleting..." : "Delete My Account"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="text-destructive">Delete Your Account?</AlertDialogTitle>
                <AlertDialogDescription>
                  This is your final confirmation. Your account, all projects, and all data will be permanently deleted.
                  This action cannot be reversed.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-white hover:bg-destructive/90"
                  onClick={handleDeleteAccount}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Deleting..." : "Yes, Delete Everything"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>

      <div className="text-center py-4">
        <p className="text-sm text-text-gray">
          Need help instead?{" "}
          <a href="/help" className="text-sm-primary hover:underline">
            Contact our support team
          </a>
          {" "}— we're happy to assist.
        </p>
      </div>
    </div>
  );
}
